import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from 'react-i18next';
import {
  Paper,
  Box,
  Button,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TextField,
} from "@mui/material";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { makeStyles } from '@material-ui/core/styles';
import CreditRadarChart from "../components/CreditRadarChart";
import IndicatorChart from "../components/IndicatorChart";
import GPALineChart from "../components/GPALineChart";
import {
  creditResponseType,
  trainingIndicatorResponseType,
  diplomaSupplementResponseType,
  gpaResponseType,
} from "../common/type";
import { useCustomMediaQuery } from "../common/useCustomMediaQuery";
import { basicColor, themeColors } from "../common/colors";
import logo from "../assets/logo_mark.png";
import sign from "../assets/sign.jpg";

// Material-UIのスタイル設定
const useStyles = makeStyles({
  chartContainer: {
    position: 'relative',
    marginTop: '15px',
    padding: '10px 20px 15px',
    overflow: 'hidden'
  },
  chartContainerSp: {
    position: 'relative',
    marginTop: '15px',
    padding: '10px 0 15px',
    overflow: 'hidden'
  },
  tableCellPropertyStyle: {
    padding: '2px 5px',
    borderBottom: '1px solid #D9D9D9',
  },
  tableCellPropertySpStyle: {
    padding: '2px 5px',
    borderBottom: '1px solid #D9D9D9',
    fontSize: '10px',
  },
});

const themeProvidertheme = createTheme({
  components: {
    MuiInputLabel: {
      styleOverrides: {
        root: {
          backgroundColor: 'white',
          padding: '0 4px',
        },
      },
    },
  },
});

const FIRST_TABLE_CELL_WIDTH = { width: '12%' };
const RADAR_CHART_SIZE: number[] = [
  600, 320
];

type DegreeType = 'UG' | 'MS' | 'Prof'; // 学部生 / 大学院・修士課程 / 大学院・専門職学位課程
type EnrollmentStatus = 'enrolled' | 'graduated'; // 在籍中 / 卒業（修了）
interface UserStatusType {
  academicDegree: DegreeType;
  enrollment: EnrollmentStatus;
};

interface userDataTypeProps {
  userData: diplomaSupplementResponseType;
  isExportingPDF: boolean;
};

interface academicRecordProps {
  userStatus: UserStatusType;
  creditData: creditResponseType;
  trainingIndicatorData: trainingIndicatorResponseType;
  gpaData: gpaResponseType;
  isExportingPDF: boolean;
};

interface diplomaSupplementTypeProps {
  userStatus: UserStatusType;
  userData: diplomaSupplementResponseType;
  creditData: creditResponseType;
  trainingIndicatorData: trainingIndicatorResponseType;
  gpaData: gpaResponseType;
  userSelfRefection: string;
  onChange: (newText: string) => void;
  isExportingPDF: boolean;
  onSaveClick: () => void;
  isReadOnly: boolean;
};

const SpaceRow = () => {
  return <tr><td colSpan={4} style={{ height: "10px" }}></td></tr>;
};

const UserDataRow = React.memo(({
  userData,
  isExportingPDF,
}: userDataTypeProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { isDesktop } = useCustomMediaQuery();
  const isDesktopStyle = isDesktop || isExportingPDF;
  const tableCellClassName = isDesktopStyle ? classes.tableCellPropertyStyle : classes.tableCellPropertySpStyle;
  // 在学期間の表記
  const periodOfEnrollment = (): string => {
    if (userData.admission_date && userData.graduation_date) {
      return `${userData.admission_date}～${userData.graduation_date}`;
    } else if (userData.admission_date) {
      return `${userData.admission_date}～${t("ds.headerEnrolling")}`;
    } else if (userData.graduation_date) {
      return `～${userData.graduation_date}`;
    } else {
      return "";
    }
  };
  return (
    <>
      <TableRow>
        <TableCell className={tableCellClassName} sx={FIRST_TABLE_CELL_WIDTH}>{t("ds.headerStudentNumber")}</TableCell>
        <TableCell className={tableCellClassName}>{`${userData.student_number} ${userData.user_name}`}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell className={tableCellClassName}>{t("ds.headerPeriod")}</TableCell>
        <TableCell className={tableCellClassName}>{periodOfEnrollment()}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell className={tableCellClassName}>{t("ds.headerDepartment")}</TableCell>
        <TableCell className={tableCellClassName}>{`${userData.department_name}　${userData.major_name}`}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell className={tableCellClassName}>{t("ds.headerMajor")}</TableCell>
        <TableCell className={tableCellClassName}>{`${userData.course_name}`}</TableCell>
      </TableRow>
    </>
  );
});

const DegreeRow = React.memo(({
  userData,
  isExportingPDF,
}: userDataTypeProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { isDesktop } = useCustomMediaQuery();
  const isDesktopStyle = isDesktop || isExportingPDF;
  const tableCellClassName = isDesktopStyle ? classes.tableCellPropertyStyle : classes.tableCellPropertySpStyle;
  return (
    <>
      <TableRow>
        <TableCell className={tableCellClassName} sx={FIRST_TABLE_CELL_WIDTH}>{t("ds.headerAcademicDegree")}</TableCell>
        <TableCell className={tableCellClassName}>{`${userData.academic_degree_name ?? ""}`}</TableCell>
      </TableRow>
    </>
  );
});

const AbilityRow = React.memo(({
  userData,
  isExportingPDF,
}: userDataTypeProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { isDesktop } = useCustomMediaQuery();
  const isDesktopStyle = isDesktop || isExportingPDF;
  const tableCellClassName = isDesktopStyle ? classes.tableCellPropertyStyle : classes.tableCellPropertySpStyle;
  return (
    <>
      <TableRow>
        <TableCell className={tableCellClassName} sx={FIRST_TABLE_CELL_WIDTH}>{t("ds.headerQuality")}</TableCell>
        <TableCell className={tableCellClassName}>{`${userData.user_quality ?? ""}`}</TableCell>
      </TableRow>
    </>
  );
});

const AcademicRecordRow = React.memo(({
  userStatus,
  creditData,
  trainingIndicatorData,
  gpaData,
  isExportingPDF
}: academicRecordProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { isDesktop } = useCustomMediaQuery();
  const isDesktopStyle = isDesktop || isExportingPDF;
  type lineLabelFontSizeType = {
    desktopFontSize: string;
    mobileFontSize: string;
  };
  const RadarLedgendFontSize = isDesktopStyle ? '0.7rem' : '0.5rem';
  const CommonGPAFontSize = '0.8rem';
  type CommonGPAFontSizeType = '0.8rem';
  type fontSizeType = {
    xAxisLabel: CommonGPAFontSizeType;
    yAxisLabel: CommonGPAFontSizeType;
    lineLabel?: lineLabelFontSizeType;
    legend: CommonGPAFontSizeType;
  };
  const GPALineLabelFontSize: lineLabelFontSizeType = {
    desktopFontSize: '1rem',
    mobileFontSize: '1rem',
  };
  const GPAFontSize: fontSizeType = {
    xAxisLabel: CommonGPAFontSize,
    yAxisLabel: CommonGPAFontSize,
    lineLabel: GPALineLabelFontSize,
    legend: CommonGPAFontSize,
  };

  // LineChartは、サイズのpropsを適切に設定しないと、Tooltipが上手く動作しなくなる
  // そのため、親の計算させてそれを元に幅を設定する
  // このコンポーネントでは、PDF出力するときはページが固定幅になるため、そのことも考慮する必要がある
  const useParentWidth = (parentRef: React.RefObject<HTMLElement>) => {
    const [width, setWidth] = useState(0);

    const handleResize = () => {
      if (parentRef.current) {
        setWidth(parentRef.current.offsetWidth);
      }
    };

    useEffect(() => {
      handleResize();
      window.addEventListener('resize', handleResize);
      // クリーンアップ関数でイベントリスナーを削除
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, [parentRef]);
    return width;
  };

  const parentRef = useRef<HTMLDivElement>(null);
  const lineChartPaddingWidth = 10;
  const lineChartMaxWidth = 1000;
  const viewWidth: number = useParentWidth(parentRef) - lineChartPaddingWidth;
  const LineChartWidth: number = isExportingPDF || viewWidth > lineChartMaxWidth ? lineChartMaxWidth : viewWidth;
  // LineChartXAxisInterval: X軸のラベルの表示の間隔
  // グラフの表示幅が狭くなってくると、ラベルの文言がぶつかってしまう。
  // そのため表示幅が狭い場合には、ラベルを1つおきに表示する。
  const LineChartXAxisInterval = isExportingPDF || LineChartWidth > 450 ? 0 : 1;

  const LineChartSize: number[] = [
    LineChartWidth, 320
  ];

  const tableCellClassName = isDesktopStyle ? classes.tableCellPropertyStyle : classes.tableCellPropertySpStyle;

  return (
    <>
      <TableRow>
        <TableCell className={tableCellClassName} sx={FIRST_TABLE_CELL_WIDTH}>{t("common.perspective")}</TableCell>
        <TableCell className={tableCellClassName}>
          <Box className={isDesktopStyle ? 'flex flex-row justify-center items-center' : ''}>
            <Box className={isDesktopStyle ? 'mx-2' : 'mx-2 mt-2 mb-5'}>
              <Paper className="text-center">{t("credit.chart_title_1")}</Paper>
              <CreditRadarChart
                data={creditData}
                target="seData"
                raderChartSize={RADAR_CHART_SIZE}
                legendFontSize={RadarLedgendFontSize}
                isExportingPDF={isExportingPDF}
              />
            </Box>
            <Box className={isDesktopStyle ? 'mx-2' : 'm-2'}>
              <Paper className="text-center">{t("credit.chart_title_2")}</Paper>
              <CreditRadarChart
                data={creditData}
                target="tsData"
                raderChartSize={RADAR_CHART_SIZE}
                legendFontSize={RadarLedgendFontSize}
                isExportingPDF={isExportingPDF}
              />
            </Box>
          </Box>
        </TableCell>
      </TableRow>

      {(userStatus.academicDegree === 'UG' || userStatus.academicDegree === 'Prof') &&
        <TableRow>
          <TableCell className={tableCellClassName} sx={FIRST_TABLE_CELL_WIDTH}>{t("ds.headerIndicator")}</TableCell>
          <TableCell className={tableCellClassName}>
            <IndicatorChart
              tiDataType="mext"
              tiData={trainingIndicatorData}
              isExportingPDF={isExportingPDF}
            />
          </TableCell>
        </TableRow>
      }
      {userStatus.academicDegree === 'UG' &&
        <TableRow>
          <TableCell className={tableCellClassName} sx={FIRST_TABLE_CELL_WIDTH}>GPA</TableCell>
          <TableCell ref={parentRef} className={tableCellClassName}>
            <GPALineChart
              chartSize={LineChartSize}
              data={gpaData}
              fontSize={GPAFontSize}
              xAxisLabelInterval={LineChartXAxisInterval}
              isExportingPDF={isExportingPDF}
            />
          </TableCell>
        </TableRow>
      }
    </>
  );
});

const DiplomaSupplementCoC = ({
  userStatus,
  userData,
  creditData,
  trainingIndicatorData,
  gpaData,
  userSelfRefection,
  onChange,
  isExportingPDF,
  onSaveClick,
  isReadOnly,
}: diplomaSupplementTypeProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { isDesktop } = useCustomMediaQuery();
  const isDesktopStyle = isDesktop || isExportingPDF;
  const researchTopic = [
    { label: t("userAchievements.arch_1"), value: userData.achievement_1 ?? "" },
    { label: t("userAchievements.arch_2"), value: userData.achievement_2 ?? "" },
    { label: t("userAchievements.arch_4"), value: userData.achievement_4 ?? "" },
    { label: t("userAchievements.arch_3"), value: userData.achievement_3 ?? "" },
    { label: t("userAchievements.arch_8"), value: userData.achievement_8 ?? "" },
  ];
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    if (value.length <= 1000) {
      onChange(value);
    }
  };

  const titleStyle = isExportingPDF ?
    {
      fontSize: '36px',
      color: '#fff',
      textAlign: 'center',
      background: basicColor,
      fontWeight: 'bold',
    } : {
      fontSize: '1rem',
      color: '#fff',
      textAlign: 'center',
      background: basicColor,
      fontWeight: 'normal',
    };


  const tableCellClassName = isDesktopStyle ? classes.tableCellPropertyStyle : classes.tableCellPropertySpStyle;

  return (
    <>
      <Paper className={
        isDesktopStyle ? classes.chartContainer : classes.chartContainerSp
      }>
        <TableContainer>
          <Table aria-label="simple table">
            <TableHead key="table-head">
              <TableRow key="table-head-row">
                <TableCell
                  sx={titleStyle}
                  colSpan={4}
                >
                  <span style={isExportingPDF ? { position: "relative", bottom: "7px" } : {}}>
                    {userData.graduation_date ? t("ds.title") : t("ds.preTitle")}
                  </span>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody key="table-body">
              <TableRow key="table-row-logo">
                <TableCell colSpan={2} sx={{ padding: "1px 0", textAlign: "right", border: "none" }}>
                  <Box my={0.5} sx={{ display: 'flex', justifyContent: 'end' }}>
                    <Box><img src={logo} style={{ display: "inline", width: '40px' }} alt="鳴門教育大学" /></Box>
                    <Box ml={2}
                      sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'start' }}>
                      <Typography sx={{ fontSize: '.875rem' }}>国立大学法人鳴門教育大学</Typography>
                      <Typography sx={{ fontSize: '.5rem' }}>Naruto University of Education</Typography>
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>
              <UserDataRow
                userData={userData}
                isExportingPDF={isExportingPDF}
              />
              <SpaceRow />
              <DegreeRow
                userData={userData}
                isExportingPDF={isExportingPDF}
              />
              <SpaceRow />
              <AbilityRow
                userData={userData}
                isExportingPDF={isExportingPDF}
              />
              <SpaceRow />
              <AcademicRecordRow
                userStatus={userStatus}
                creditData={creditData}
                trainingIndicatorData={trainingIndicatorData}
                gpaData={gpaData}
                isExportingPDF={isExportingPDF}
              />
            </TableBody>
          </Table>
        </TableContainer>
        {t("common.chartNotation")}
        <Box className={isDesktopStyle ? 'flex flex-row justify-between' : ''}>
          <Box className="flex flex-col justify-start items-start w-1/2">
            {/* 顧客要望で削除 (2025/10/10)
              <Box className="flex flex-row justify-between items-center w-64">
                <Typography>{t("ds.headerDegreeDate")}</Typography>
                <Typography>{userData.degree_issued_date}</Typography>
              </Box>
            */}
            <Box className="flex flex-row justify-between items-center w-64">
              <Typography>{userData.graduation_date ? t("ds.headerCertificationDate") : t("ds.headerCertificationDatePre")}</Typography>
              <Typography>{userData.degree_issued_date}</Typography>
            </Box>
          </Box>
          {/* 顧客要望対応：プレでなくDSの時だけ、認定文言や公印を表示する (2025/10/10) */}
          {userData.graduation_date ? <>
            <Box className={isDesktopStyle ? 'flex flex-col justify-start items-end w-1/2 mr-8' : 'my-2'}>
              <Typography>{t("ds.certificationText")}</Typography>
              <Typography>{t("ds.president")}</Typography>
              <Typography>{t("ds.presidentName")}</Typography>
            </Box>
            <Box mt={1} mr={1}>
              <img src={sign} style={{ width: '80px' }} alt="学校のサイン" />
            </Box>
          </> : <>
            <Box className={isDesktopStyle ? 'flex flex-col justify-start items-end w-1/2 mr-8' : 'my-2'}>
              <Typography>&nbsp;</Typography>
              <Typography>&nbsp;</Typography>
              <Typography>&nbsp;</Typography>
            </Box>
          </>}
        </Box>
        <Typography style={{ color: themeColors[0] }}>{t("ds.achievementsText")}</Typography>
        <TableContainer>
          <Table aria-label="simple table">
            <TableBody key="table-body">
              {researchTopic && researchTopic.map((item: any, index: number) => (
                <TableRow key={`${item.label}-${index}`}>
                  <TableCell className={tableCellClassName} sx={FIRST_TABLE_CELL_WIDTH}>{item.label}</TableCell>
                  <TableCell className={tableCellClassName}>{item.value}</TableCell>
                </TableRow>
              ))}
              <TableRow><TableCell sx={{ borderBottom: 'unset' }} /></TableRow>
              <TableRow>
                <TableCell className={tableCellClassName}>{t("ds.selfReflection")}</TableCell>
                <TableCell className={tableCellClassName}>
                  {!isExportingPDF ?
                    <Box className="w-full">
                      <ThemeProvider theme={themeProvidertheme}>
                        <TextField
                          label={`${t("ds.selfReflectionNote")} (${userSelfRefection.length}/1000)`}
                          multiline
                          rows={4}
                          variant="outlined"
                          value={userSelfRefection}
                          onChange={handleChange}
                          inputProps={{ maxLength: 1000 }}
                          InputProps={{
                            style: { resize: 'none' }
                          }}
                          style={{ width: '100%' }}
                          disabled={isReadOnly}
                        />
                      </ThemeProvider>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          sx={{
                            margin: '3px 0',
                            padding: '6px 24px',
                            backgroundColor: '#FFFFFF',
                            color: '#000',
                            border: '1px solid #D9D9D9',
                            borderRadius: '20px'
                          }}
                          disabled={isReadOnly}
                          onClick={onSaveClick}
                        >{t("button.save")}</Button>
                      </Box>
                    </Box> :
                    `${userSelfRefection ?? ""}`
                  }
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  );
};

export default DiplomaSupplementCoC;